import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AvailabilityCalendar from './AvailabilityCalendar';
import ReservationForm from './ReservationForm';
import { fetchAvailability, fetchCurrentTerms } from './reservationApi';
import './ReservationPanel.css';

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function shiftMonth(month, delta) {
  const [year, monthNumber] = month.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, monthNumber - 1 + delta, 1));
  return shifted.toISOString().slice(0, 7);
}

function ReservationPanel({ vehicleSlug }) {
  const [unavailableDates, setUnavailableDates] = useState(null);
  const [termsVersion, setTermsVersion] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [month, setMonth] = useState(currentMonth);
  const [selection, setSelection] = useState({ startDate: '', endDate: '' });

  useEffect(() => {
    let cancelled = false;

    fetchAvailability(vehicleSlug)
      .then((dates) => {
        if (!cancelled) {
          setUnavailableDates(dates);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
        }
      });

    // A reservation cannot be submitted without a valid, current terms
    // version, so its failure blocks the panel exactly like availability's does.
    fetchCurrentTerms()
      .then((current) => {
        if (!cancelled) {
          setTermsVersion(current.version);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
        }
      });

    // Guards against a late response from the previous vehicle overwriting the
    // current one's dates when the visitor navigates between detail pages.
    return () => {
      cancelled = true;
    };
  }, [vehicleSlug]);

  const handleSelect = useCallback((date) => {
    setSelection((previous) => {
      if (!previous.startDate || previous.endDate) {
        return { startDate: date, endDate: '' };
      }
      if (date < previous.startDate) {
        return { startDate: date, endDate: '' };
      }
      return { ...previous, endDate: date };
    });
  }, []);

  if (loadFailed) {
    return (
      <section className="reservation-panel">
        <h2>Request to Reserve</h2>
        <p role="alert">
          We could not load availability for this vehicle right now.{' '}
          <Link to="/about">Contact us</Link> and we will check the dates for you.
        </p>
      </section>
    );
  }

  if (unavailableDates === null || termsVersion === null) {
    return (
      <section className="reservation-panel">
        <h2>Request to Reserve</h2>
        <p>Loading availability...</p>
      </section>
    );
  }

  return (
    <section className="reservation-panel">
      <div className="reservation-panel__months">
        <button
          type="button"
          className="reservation-panel__month-nav"
          onClick={() => setMonth((previous) => shiftMonth(previous, -1))}
          disabled={month <= currentMonth()}
        >
          Previous month
        </button>
        <button
          type="button"
          className="reservation-panel__month-nav"
          onClick={() => setMonth((previous) => shiftMonth(previous, 1))}
        >
          Next month
        </button>
      </div>
      <AvailabilityCalendar
        month={month}
        unavailableDates={unavailableDates}
        selection={selection}
        onSelect={handleSelect}
      />
      <ReservationForm
        vehicleSlug={vehicleSlug}
        unavailableDates={unavailableDates}
        selection={selection}
        termsVersion={termsVersion}
      />
    </section>
  );
}

export default ReservationPanel;
